import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { TruncatedText } from '@/components/common/truncated-text';
import { MultipleContainers } from '@/components/ui/dnd-kit/MultipleContainers';
import { MaterialIcon } from './assets/icon/material-icon';
import { ActorNewIcon, FolderIcon, KnowledgeFileIcon, KnowledgeIcon, VoiceNewIcon } from './assets';
import { assetsKnowledgeHooks, assetsMaterialHooks } from '@/manager';
import { ChevronRight, Plus } from 'lucide-react';
import { IconButton } from '../common/icon-button';
import { Spinner, Tooltip } from '../ui/modal';
import { useState } from 'react';
import { getNextName } from '@/utils/get-next-name';

// 骨架屏容器组件
function Skeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className='w-60 h-7 flex items-center px-4 py-2 rounded-sm transition-colors animate-pulse'>
          <div className="flex-1 h-7 bg-gray-200 animate-pulse rounded-md"></div>
        </div>
      ))}
    </div>
  );
}

/** 资产侧边栏 */
export const AssetsSidebar = ({
  collapsed = false,
}: {
  collapsed?: boolean;
}) => {
  const { t } = useTranslation();
  const [triggerId, setTriggerId] = useState('');

  const {
    sidebar,
    materialFolders,
    knowledgeFolders,
    isAgentData,
    sidebarMap,
    sidebarKeyMap,
    childrenKeyMap,
    active,
    sidebarCollapsed,

    handleParentClick,
    handleChildrenClick,
    handleCollapseChange,
  } = assetsMaterialHooks.useAssetsSidebarData()

  const { createMaterial, loading: createMaterialLoading } = assetsMaterialHooks.useMaterialFolderCreate();
  const { createKnowledge, loading: createKnowledgeLoading } = assetsKnowledgeHooks.useKnowledgeAdd();

  // 统一的文件夹创建处理器
  const handleCreateFolder = async (type: 'material' | 'knowledge') => {
    setTriggerId(type);

    const isMaterial = type === 'material';
    const prefix = isMaterial ? t('assets.material.untitled') : t('assets.knowledge.untitled');
    const targetFolders = isMaterial ? materialFolders : knowledgeFolders;
    const createFn = isMaterial ? createMaterial : createKnowledge;

    try {
      const nextName = getNextName(targetFolders, prefix);
      await createFn({ name: nextName });
    } catch (error) {
      console.error(`Failed to create ${type} folder:`, error);
    }
  };

  // 渲染一级选项的图标组件
  const renderItemIcon = (id: string) => {
    const iconClassName = cn(
      'size-5 text-[rgba(13,13,13,1)]',
      // active === id ? 'text-[rgba(16,24,40,1)]' : 'text-[rgba(52,64,84,1)]'
    );

    switch (id) {
      case 'material': return <MaterialIcon className={iconClassName} />;
      case 'knowledge': return <KnowledgeIcon className={iconClassName} />;
      case 'actor': return <ActorNewIcon className={iconClassName} />;
      case 'voice': return <VoiceNewIcon className={iconClassName} />;
      default: return <MaterialIcon className={iconClassName} />;
    }
  };

  return (
    <section
      className={cn("h-full transition-all duration-300")}
    >
      <div className="flex-1 space-y-4 overflow-y-auto scrollbar-hide flex-shrink-0">
        <MultipleContainers
          disabled
          items={sidebarMap}
          // 整体容器
          renderFolders={(children) => (
            <div className={cn(
              "space-y-0.5",
            )}>
              <div className={cn(
                "pl-2 text-sm py-1.5 text-[rgba(102,112,133,1)] whitespace-nowrap",
                collapsed && "hidden"
              )}>
                {t('assets.title')}
              </div>
              {children}
            </div>
          )}
          // 渲染一级选项
          renderContainer={(id: string) => {
            const hasChildren = !!sidebarKeyMap?.[id]?.children;
            const isLibrary = id === 'material' || id === 'knowledge'
            return (
              <div
                className={cn(
                  'group relative flex items-center p-2 py-1.5 rounded-[6px] transition-colors duration-200 hover:bg-[rgba(16,24,40,0.05)] cursor-pointer',
                  active === id && 'bg-[rgba(16,24,40,0.05)]',
                  collapsed && 'justify-center p-0' // 折叠时居中
                )}
                onClick={() => handleParentClick(id)}
              >
                {/* 图标/按钮 混合容器 */}
                <div className={cn(
                  "relative size-8 flex items-center justify-center w-6 h-6 mr-2 flex-shrink-0",
                  collapsed ? "size-9 mr-0 flex-shrink-0" : "mr-2"
                )}>
                  {/* 折叠按钮：Hover 时显示 */}
                  {isAgentData && !collapsed && hasChildren && (
                    <IconButton
                      className={cn(
                        'absolute inset-0 flex items-center justify-center p-0 h-6 w-6 hover:bg-[rgba(16,24,40,0.08)] transition-all duration-200 ease-in-out',
                        'opacity-0 group-hover:opacity-100 z-10'
                      )}
                      icon={
                        <ChevronRight
                          className={cn(
                            'size-5 transition-transform duration-300',
                            sidebarCollapsed?.[id] ? 'rotate-90' : 'rotate-0',
                            active !== id && 'text-[rgba(52,64,84,1)]',
                          )}
                        />
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCollapseChange(id);
                      }}
                    />
                  )}

                  {/* 业务图标：非 Hover 时显示 */}
                  <div className={cn(
                    'transition-all duration-300 ease-in-out flex items-center justify-center',
                    (isAgentData && !collapsed && hasChildren) ? 'group-hover:opacity-0' : 'opacity-100'
                  )}>
                    <Tooltip
                      side='right'
                      align='center'
                      offset={25}
                      trigger={
                        <div>{renderItemIcon(id)}</div>
                      }
                      content={
                        collapsed && (
                          <div className="relative max-w-[384px] w-max bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]">
                            <span className="block max-w-[384px] text-[rgba(255,255,255,1)] text-sm font-medium whitespace-nowrap">
                              {sidebarKeyMap[id]?.name}
                            </span>
                          </div>
                        )
                      }
                    />
                  </div>
                </div>
                {/* 文字和新增按钮*/}
                {!collapsed && (
                  <>
                    <div className="text-sm text-[#0D0D0D] select-none whitespace-nowrap">
                      {sidebarKeyMap[id]?.name}
                    </div>
                    {isAgentData && isLibrary && (
                      !createMaterialLoading && !createKnowledgeLoading ? (
                        <IconButton
                          className='absolute right-2 size-6 opacity-0 group-hover:opacity-100 transition-all hover:bg-[rgba(16,24,40,0.08)]'
                          icon={<Plus className='size-5' />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCreateFolder(id as 'material' | 'knowledge');
                          }}
                        />
                      ) : (
                        triggerId === id && <Spinner className='size-5 absolute right-2' />
                      )
                    )}
                  </>
                )}
              </div>
            );
          }}
          /// 渲染二级选项
          renderItem={(id: string, pid?: string) => {
            const Icon = pid === 'material' ? FolderIcon : KnowledgeFileIcon
            const isMore = id === 'more'

            if (collapsed) return null;

            return sidebarCollapsed[pid!] ? (
              <div
                className={cn(
                  'group flex items-center p-2 rounded-sm cursor-pointer pl-10 select-none hover:bg-[rgba(16,24,40,0.05)] transition-all ease-in-out mt-0.5',
                  active === id && 'bg-[rgba(16,24,40,0.05)]'
                )}
                onClick={() => handleChildrenClick(id, pid!)}
              >
                {!isMore && (
                  <Icon className={cn(
                    'size-5 inline-block mr-2 text-[rgba(52,64,84,1)] flex-shrink-0',
                    active === id && 'text-[rgba(16,24,40,1)]',
                  )} />
                )}
                <TruncatedText text={childrenKeyMap?.[id]?.name} className={cn(
                  "text-sm text-[#0D0D0D] transition-all whitespace-nowrap",
                  isMore && 'text-[rgba(13,13,13,1)] opacity-60 group-hover:opacity-100',
                )} />
              </div>
            ) : null
          }}
          renderDragOverlayItem={(id: string, pid?: string) => null}
          onContainerReorder={(_, ids) => null}
          onItemReorder={() => { }}
        />
      </div>
    </section>
  )
}